import { AsyncSubject, Observable } from 'rxjs';
import { AxiosRequestHeaders, AxiosResponse, Method } from 'axios';
import authService from './authService';
import {
  AxiosRequestConfig,
  createAxiosHttpClient,
  isAbortedRequestError,
  RetryStrategy,
} from './httpService.utils';

interface HttpRequest {
  method: Method;
  url: string;
  headers?: AxiosRequestHeaders;
  data?: any;
  signal?: AbortSignal;
  // Enable/disable retries for this request.
  retry?: boolean;
  // 'exponential' (default) or 'basic' (immediate retry, no backoff).
  retryStrategy?: RetryStrategy;
}

const axiosClient = createAxiosHttpClient();

class HttpService {
  // GET requests that are currently in flight, keyed by URL. Entries are
  // removed as soon as the request settles, so this only ever collapses
  // *concurrent* duplicate GETs — it is not a response cache and cannot serve
  // stale data. Several screens dispatch the same fetch from more than one
  // component on mount; this makes those a single network round trip.
  private readonly inFlightGets = new Map<string, Observable<any>>();

  public getJSON(
    url: string,
    signal?: AbortSignal,
    retry: boolean = true,
    retryStrategy?: RetryStrategy,
  ): Observable<any> {
    // Never share a request that carries an AbortSignal: one caller aborting
    // must not cancel the request out from under an unrelated caller.
    if (signal) {
      return this.sendGet(url, signal, retry, retryStrategy);
    }

    const inFlight = this.inFlightGets.get(url);
    if (inFlight) {
      return inFlight;
    }

    const request = this.sendGet(url, undefined, retry, retryStrategy);
    this.inFlightGets.set(url, request);

    // AsyncSubject replays its value to late subscribers, so callers that
    // subscribe after completion still get the response.
    const clear = () => this.inFlightGets.delete(url);
    request.subscribe({ complete: clear, error: clear });

    return request;
  }

  private sendGet(
    url: string,
    signal: AbortSignal | undefined,
    retry: boolean,
    retryStrategy?: RetryStrategy,
  ): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'GET',
      url,
      signal,
      retry,
      retryStrategy,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public postJSON(
    url: string,
    data: any,
    formData: boolean = false,
    signal?: AbortSignal,
    retry: boolean = false,
    retryStrategy?: RetryStrategy,
  ): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'POST',
      url,
      data,
      signal,
      retry,
      retryStrategy,
    };
    this.makeRequest(request, subject, formData);

    return subject;
  }

  public putJSON(
    url: string,
    data: any,
    retry: boolean = false,
    retryStrategy?: RetryStrategy,
  ): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'PUT',
      url,
      data,
      retry,
      retryStrategy,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public patchJSON(
    url: string,
    data: any,
    retry: boolean = false,
    retryStrategy?: RetryStrategy,
  ): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'PATCH',
      url,
      data,
      retry,
      retryStrategy,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public deleteJSON(
    url: string,
    retry: boolean = false,
    retryStrategy?: RetryStrategy,
  ): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'DELETE',
      url,
      retry,
      retryStrategy,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public getQueryParams(params: object): string {
    return Object.entries(params)
      .filter(
        ([_, value]) =>
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0),
      )
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${encodeURIComponent(key)}=${encodeURIComponent((value as string[]).join(','))}`;
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value?.toString() ?? '')}`;
      })
      .join('&');
  }

  private makeRequest(
    request: HttpRequest,
    subject: AsyncSubject<AxiosResponse>,
    formData: boolean = false,
  ) {
    this.getOptions(request, formData).then((options) => {
      axiosClient
        .request(options)
        .then((resp) => {
          this.resolveRequest(resp, subject);
        })
        .catch((err) => {
          if (
            isAbortedRequestError(err) &&
            (err.response === undefined || err.response === null)
          ) {
            subject.error(err);
            return;
          }
          if (err.response?.status === 401) {
            authService.logout();
          }
          this.resolveRequest(err.response, subject);
        });
    });
  }

  private resolveRequest(response: AxiosResponse, subject: AsyncSubject<any>) {
    if (this.isValid(response)) {
      const data = response.data.data ? response.data.data : response.data;
      subject.next(data);
      subject.complete();
    } else {
      subject.error(response?.data || 'HTTP request failed');
    }
  }

  private getOptions(
    request: HttpRequest,
    formData: boolean,
  ): Promise<AxiosRequestConfig> {
    return new Promise((resolve) => {
      this.getHeaders(formData).then((headers) => {
        const options: AxiosRequestConfig = {
          method: request.method,
          url: request.url,
          headers,
          data: request.data ? request.data : undefined,
          signal: request.signal,
          retry: request.retry,
          retryStrategy: request.retryStrategy,
        };
        resolve(options);
      });
    });
  }

  private getHeaders(formData: boolean): Promise<any> {
    const { __environment__ } = window as any;
    return new Promise((done) => {
      const newHeaders = {
        Accept: 'application/json',
        'X-App-Bc': __environment__.REACT_APP_CLIENT_BC?.trim(),
        // `X-Address-Validation-Bc: getPortalLabel()` belongs here, but this
        // environment's API does not allow it. Istio replies with a fixed
        // `Access-Control-Allow-Headers: Authorization, AuthorizationID,
        // X-App-Bc, Content-Type`, so the browser blocks every request that
        // carries the extra header -- `users/me` fails, and the app sends the
        // user to /noaccess. Note the preflight itself still answers 200; the
        // enforcement is client-side, which is why curl sees a healthy API.
        //
        // To restore it: add `X-Address-Validation-Bc` to the `allowHeaders`
        // list in all 17 CORS blocks under `apis/*/deploy/<env>/*.yaml`, let
        // ArgoCD roll that out, then re-add the header here and drop this note.
        'Content-Type': formData
          ? 'multipart/form-data'
          : 'application/json;charset=UTF-8',
      };

      authService.getTokens().subscribe((tokens) => {
        if (tokens.accessToken) {
          (newHeaders as any).Authorization = `Bearer ${tokens.accessToken}`;
        }
        if (tokens.idToken) {
          (newHeaders as any).AuthorizationID = `${tokens.idToken}`;
        }

        done(newHeaders);
      });
    });
  }

  private isValid(response: AxiosResponse): boolean {
    return response?.status >= 200 && response?.status < 300;
  }
}

const httpService = new HttpService();

export default httpService;
